import { useCallback, useState, useEffect } from 'react';
import ZoomVideo, { VideoQuality } from '@zoom/videosdk';
import { axiosPrivate } from '../api/axios';
import { getSocket } from '../services/socket';
import {
    getPreJoinDevicePreferences,
    clearPreJoinDevicePreferences,
} from '../utils/preJoinMediaPreferences';
import {
    getLiveClassState,
    patchLiveClassState,
    resetLiveClassState,
    subscribeLiveClass,
    mapRecordingStatus,
    CONNECTION_STATE,
    RECORDING_STATE,
} from './liveClassStore';
import { logLiveClassEvent, setLiveClassLogContext } from '../utils/liveClassLogger';
import {
    canMuteOthers,
    canControlRecording,
    canFreezeScreen,
    permissionError,
} from '../utils/liveClassPermissions';

const isZoomEnabled = () => process.env.REACT_APP_ZOOM_ENABLED === 'true';

/**
 * Singleton del cliente Video SDK (solo una sesión a la vez).
 * createClient es singleton: tras leave hay que destroyClient antes de reiniciar.
 * StrictMode: cancelJoin evita doble join / cierre prematuro.
 */
const videoSession = {
    client: null,
    stream: null,
    joinedKey: null,
    joinPromise: null,
    leavePromise: null,
    cancelJoin: false,
    suppressClosedNotify: false,
    closedCallbacks: new Set(),
    participantListeners: new Set(),
    readyListeners: new Set(),
    /** Últimas credenciales usables para reconectar de forma transparente. */
    lastCredentials: null,
    reconnectTimer: null,
    reconnectAttempt: 0,
    reconnecting: false,
    reconnectDisabled: false,
    /** Intención del usuario: se reafirma tras share y tras reconectar. */
    desired: { audioOn: false, videoOn: false },
    recoveringCapture: false,
};

/** Cierres provocados a propósito por el anfitrión: no se reconecta. */
const INTENTIONAL_CLOSE_REASONS = ['ended by host', 'kicked by host', 'expeled by host'];
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];

const notifyReady = (client) => {
    videoSession.readyListeners.forEach((cb) => {
        try {
            cb(client);
        } catch (err) {
            console.warn('[VideoSDK] ready listener:', err?.message || err);
        }
    });
};

const getSessionKey = (credentials) => (
    `${credentials.sessionName}:${credentials.signature}`
);

const mapUser = (u) => ({
    userId: u.userId,
    displayName: u.displayName || u.userName || 'Participante',
    bVideoOn: Boolean(u.bVideoOn),
    muted: Boolean(u.muted),
    audio: u.audio,
    isHost: Boolean(u.isHost),
    isManager: Boolean(u.isManager),
    sharerOn: Boolean(u.sharerOn),
    isSelf: false,
});

const getParticipantsSnapshot = () => {
    const client = videoSession.client;
    if (!client) return [];
    try {
        const me = client.getCurrentUserInfo();
        return (client.getAllUser() || []).map((u) => ({
            ...mapUser(u),
            isSelf: me ? u.userId === me.userId : false,
        }));
    } catch {
        return [];
    }
};

const notifyParticipants = () => {
    const list = getParticipantsSnapshot();
    patchLiveClassState({ participants: list });
    videoSession.participantListeners.forEach((cb) => {
        try {
            cb(list);
        } catch (err) {
            console.warn('[VideoSDK] participant listener:', err?.message || err);
        }
    });
};

const notifyClosed = () => {
    if (videoSession.suppressClosedNotify) return;
    videoSession.closedCallbacks.forEach((cb) => {
        try {
            cb();
        } catch (err) {
            console.warn('[VideoSDK] Error en callback de cierre:', err?.message || err);
        }
    });
};

let activeSpeakerId = null;
const activeSpeakerListeners = new Set();
let activeShareUserId = null;
const activeShareListeners = new Set();
let cloudRecordingStatus = 'Stopped';
const recordingListeners = new Set();
const unmuteRequestListeners = new Set();
const forcedMuteListeners = new Set();

/** Timers de decaimiento del indicador «está hablando». */
const SPEAKING_DECAY_MS = 1500;
const speakingTimers = new Map();

const notifyRecording = () => {
    recordingListeners.forEach((cb) => {
        try {
            cb(cloudRecordingStatus);
        } catch {
            // ignore
        }
    });
};

const setRecordingStatusValue = (status) => {
    cloudRecordingStatus = status || 'Stopped';
    patchLiveClassState({ recordingState: mapRecordingStatus(cloudRecordingStatus) });
    notifyRecording();
};

const readRecordingStatus = (client = videoSession.client) => {
    try {
        const status = client?.getRecordingClient?.()?.getCloudRecordingStatus?.();
        return status || 'Stopped';
    } catch {
        return 'Stopped';
    }
};

const onRecordingChange = (payload) => {
    const next = payload?.state || readRecordingStatus() || 'Stopped';
    logLiveClassEvent('recording-change', { state: next });
    setRecordingStatusValue(next);
};

const notifyActiveShare = () => {
    patchLiveClassState({ activeShareUserId });
    activeShareListeners.forEach((cb) => {
        try {
            cb(activeShareUserId);
        } catch {
            // ignore
        }
    });
};

const resolveActiveShareUserId = (client = videoSession.client, stream = videoSession.stream) => {
    try {
        const fromStream = Number(stream?.getActiveShareUserId?.() || 0);
        if (fromStream) return fromStream;
        const listed = (stream?.getShareUserList?.() || []).find((u) => u?.userId);
        if (listed?.userId) return listed.userId;
        const sharer = (client?.getAllUser?.() || []).find((u) => u?.sharerOn && u.userId);
        if (sharer?.userId) return sharer.userId;
    } catch {
        // ignore
    }
    return null;
};

const syncActiveShareFromSdk = () => {
    const detected = resolveActiveShareUserId();
    if (!detected) return false;
    if (activeShareUserId !== detected) {
        activeShareUserId = detected;
        notifyActiveShare();
    }
    return true;
};

const probeActiveShare = async () => {
    for (const delay of [0, 400, 1200, 2800]) {
        if (delay) {
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
        if (!videoSession.client || !videoSession.stream) return;
        if (syncActiveShareFromSdk()) return;
    }
};

const onShareContentChange = () => {
    syncActiveShareFromSdk();
};

const onUsersChanged = () => {
    notifyParticipants();
    syncActiveShareFromSdk();
    syncSelfState();
};

const onPeerVideo = () => notifyParticipants();

const clearSpeakingTimers = () => {
    speakingTimers.forEach((timer) => clearTimeout(timer));
    speakingTimers.clear();
};

/**
 * `active-speaker` llega como ráfaga; se mantiene cada userId marcado un rato
 * más para que el recuadro no parpadee entre palabras.
 */
const onActiveSpeaker = (payload) => {
    const list = Array.isArray(payload) ? payload : [payload];
    const speaking = list.map((p) => p?.userId).filter((id) => id != null);

    const first = speaking[0] ?? null;
    if (first !== activeSpeakerId && first != null) {
        activeSpeakerId = first;
        patchLiveClassState({ activeSpeakerId });
        activeSpeakerListeners.forEach((cb) => {
            try {
                cb(activeSpeakerId);
            } catch {
                // ignore
            }
        });
    }

    speaking.forEach((userId) => {
        const existing = speakingTimers.get(userId);
        if (existing) clearTimeout(existing);
        speakingTimers.set(userId, setTimeout(() => {
            speakingTimers.delete(userId);
            patchLiveClassState({ speakingUserIds: Array.from(speakingTimers.keys()) });
        }, SPEAKING_DECAY_MS));
    });

    patchLiveClassState({ speakingUserIds: Array.from(speakingTimers.keys()) });
};

const handleActiveShareChangeEvent = (payload) => {
    const state = payload?.state;
    const userId = payload?.userId;
    if (state === 'Active') {
        activeShareUserId = userId ?? null;
    } else if (state === 'Inactive') {
        // Un share en pausa (pantalla congelada) sigue vivo: soltar la vista borraría el último fotograma.
        let paused = false;
        try {
            const sharer = userId != null ? videoSession.client?.getUser?.(userId) : null;
            paused = Boolean(sharer?.sharerOn && sharer?.sharerPause);
        } catch {
            paused = false;
        }
        if (!paused && (activeShareUserId === userId || userId == null)) {
            activeShareUserId = null;
        }
    }
    notifyActiveShare();
};

const onPassivelyStopShare = (payload) => {
    logLiveClassEvent('share-stopped-passively', { reason: payload?.reason });
    activeShareUserId = null;
    patchLiveClassState({ screenFrozen: false });
    notifyActiveShare();
    syncSelfState();
    void reassertDesiredMedia('share-stop');
};

const onNetworkQualityChange = (payload) => {
    const userId = payload?.userId;
    const type = payload?.type;
    if (userId == null || !type) return;
    const current = getLiveClassState().networkQuality;
    const prev = current[userId] || {};
    if (prev[type] === payload.level) return;
    patchLiveClassState({
        networkQuality: {
            ...current,
            [userId]: { ...prev, [type]: payload.level },
        },
    });
};

const onHostAskUnmuteAudio = (payload) => {
    logLiveClassEvent('host-ask-unmute-audio', { reason: payload?.reason });
    unmuteRequestListeners.forEach((cb) => {
        try {
            cb(payload);
        } catch {
            // ignore
        }
    });
};

const onCurrentAudioChange = (payload) => {
    const source = payload?.source;
    if (payload?.action === 'muted' && String(source || '').startsWith('passive')) {
        logLiveClassEvent('audio-muted-by-host', { reason: source });
        videoSession.desired.audioOn = false;
        forcedMuteListeners.forEach((cb) => {
            try {
                cb(payload);
            } catch {
                // ignore
            }
        });
    }
    syncSelfState();
    notifyParticipants();
};

const onVideoCapturingChange = (payload) => {
    const state = payload?.state;
    if (state === 'Failed') {
        logLiveClassEvent('video-capture-failed', {});
        void recoverVideoCapture();
        return;
    }
    if (state === 'Stopped' && videoSession.desired.videoOn) {
        logLiveClassEvent('video-capture-stopped-unexpected', {});
        void recoverVideoCapture();
        return;
    }
    syncSelfState();
};

/** Un solo reintento: si el SDK apagó la cámara sola, se vuelve a encender. */
const recoverVideoCapture = async () => {
    if (videoSession.recoveringCapture) return;
    if (!videoSession.desired.videoOn) return;
    videoSession.recoveringCapture = true;
    try {
        await new Promise((resolve) => setTimeout(resolve, 800));
        const stream = videoSession.stream;
        const me = videoSession.client?.getCurrentUserInfo?.();
        if (!stream || !me || me.bVideoOn) return;
        await stream.startVideo({ mirrored: true });
        logLiveClassEvent('video-capture-recovered', {});
    } catch (err) {
        logLiveClassEvent('video-capture-recover-failed', {
            reason: err?.reason || err?.message || String(err),
        });
    } finally {
        videoSession.recoveringCapture = false;
        syncSelfState();
    }
};

/**
 * Reafirma mic/cámara según la intención del usuario. Compartir pantalla puede
 * hacer que el SDK apague la captura; esto lo devuelve al estado esperado.
 */
const reassertDesiredMedia = async (tag = '') => {
    const stream = videoSession.stream;
    const client = videoSession.client;
    if (!stream || !client) return;
    const me = client.getCurrentUserInfo?.();
    if (!me) return;

    if (videoSession.desired.videoOn && !me.bVideoOn) {
        try {
            await stream.startVideo({ mirrored: true });
            logLiveClassEvent('reassert-video', { reason: tag });
        } catch (err) {
            logLiveClassEvent('reassert-video-failed', {
                reason: err?.reason || err?.message || String(err),
            });
        }
    }

    const meAfter = client.getCurrentUserInfo?.();
    if (videoSession.desired.audioOn && meAfter?.muted) {
        try {
            await stream.unmuteAudio();
            logLiveClassEvent('reassert-audio', { reason: tag });
        } catch (err) {
            logLiveClassEvent('reassert-audio-failed', {
                reason: err?.reason || err?.message || String(err),
            });
        }
    }

    syncSelfState();
    notifyParticipants();
};

const cancelReconnect = () => {
    if (videoSession.reconnectTimer) {
        clearTimeout(videoSession.reconnectTimer);
        videoSession.reconnectTimer = null;
    }
    videoSession.reconnectAttempt = 0;
    patchLiveClassState({ reconnectAttempt: 0 });
};

const onConnectionChange = (payload) => {
    const state = payload?.state;
    const reason = payload?.reason;
    logLiveClassEvent('connection-change', { state, reason });

    if (state === 'Connected') {
        cancelReconnect();
        patchLiveClassState({ connection: CONNECTION_STATE.CONNECTED });
        syncSelfState();
        notifyParticipants();
        return;
    }

    if (state === 'Reconnecting') {
        // Failover interno del SDK (también ocurre al entrar/salir de subsesiones).
        patchLiveClassState({ connection: CONNECTION_STATE.RECONNECTING });
        return;
    }

    if (state !== 'Closed' && state !== 'Fail') return;

    const intentional = INTENTIONAL_CLOSE_REASONS.includes(String(reason || ''));
    const canRetry = Boolean(videoSession.lastCredentials) && !videoSession.reconnectDisabled;

    videoSession.joinedKey = null;
    videoSession.stream = null;
    activeShareUserId = null;
    setRecordingStatusValue('Stopped');
    notifyActiveShare();
    notifyReady(null);

    if (intentional || !canRetry) {
        logLiveClassEvent('session-closed', { reason, intentional });
        patchLiveClassState({
            connection: CONNECTION_STATE.CLOSED,
            screenShareEnabled: false,
            screenFrozen: false,
        });
        notifyClosed();
        return;
    }

    logLiveClassEvent('session-closed-unexpected', { reason, state });
    patchLiveClassState({
        connection: CONNECTION_STATE.RECONNECTING,
        screenShareEnabled: false,
        screenFrozen: false,
    });
    scheduleReconnect();
};

const attachClientListeners = (client) => {
    client.on('user-added', onUsersChanged);
    client.on('user-removed', onUsersChanged);
    client.on('user-updated', onUsersChanged);
    client.on('peer-video-state-change', onPeerVideo);
    client.on('active-speaker', onActiveSpeaker);
    client.on('connection-change', onConnectionChange);
    client.on('active-share-change', handleActiveShareChangeEvent);
    client.on('share-content-change', onShareContentChange);
    client.on('passively-stop-share', onPassivelyStopShare);
    client.on('recording-change', onRecordingChange);
    client.on('network-quality-change', onNetworkQualityChange);
    client.on('host-ask-unmute-audio', onHostAskUnmuteAudio);
    client.on('current-audio-change', onCurrentAudioChange);
    client.on('video-capturing-change', onVideoCapturingChange);
};

const detachClientListeners = (client) => {
    if (!client) return;
    try {
        client.off('user-added', onUsersChanged);
        client.off('user-removed', onUsersChanged);
        client.off('user-updated', onUsersChanged);
        client.off('peer-video-state-change', onPeerVideo);
        client.off('active-speaker', onActiveSpeaker);
        client.off('connection-change', onConnectionChange);
        client.off('active-share-change', handleActiveShareChangeEvent);
        client.off('share-content-change', onShareContentChange);
        client.off('passively-stop-share', onPassivelyStopShare);
        client.off('recording-change', onRecordingChange);
        client.off('network-quality-change', onNetworkQualityChange);
        client.off('host-ask-unmute-audio', onHostAskUnmuteAudio);
        client.off('current-audio-change', onCurrentAudioChange);
        client.off('video-capturing-change', onVideoCapturingChange);
    } catch {
        // ignore
    }
};

export const onVideoSessionClosed = (callback) => {
    videoSession.closedCallbacks.add(callback);
    return () => videoSession.closedCallbacks.delete(callback);
};

export const onVideoParticipantsChange = (callback) => {
    videoSession.participantListeners.add(callback);
    callback(getParticipantsSnapshot());
    return () => videoSession.participantListeners.delete(callback);
};

export const onActiveSpeakerChange = (callback) => {
    activeSpeakerListeners.add(callback);
    callback(activeSpeakerId);
    return () => activeSpeakerListeners.delete(callback);
};

export const onActiveShareChange = (callback) => {
    activeShareListeners.add(callback);
    callback(activeShareUserId);
    return () => activeShareListeners.delete(callback);
};

export const onCloudRecordingChange = (callback) => {
    recordingListeners.add(callback);
    callback(cloudRecordingStatus);
    return () => recordingListeners.delete(callback);
};

/** El anfitrión pidió activar el micrófono (requiere consentimiento del alumno). */
export const onUnmuteRequest = (callback) => {
    unmuteRequestListeners.add(callback);
    return () => unmuteRequestListeners.delete(callback);
};

/** El anfitrión silenció a este usuario. */
export const onForcedMute = (callback) => {
    forcedMuteListeners.add(callback);
    return () => forcedMuteListeners.delete(callback);
};

/** Silencia a un participante (solo anfitrión). */
export const muteParticipant = async (userId) => {
    if (!canMuteOthers(getLiveClassState())) {
        throw permissionError('silenciar participantes');
    }
    const stream = videoSession.stream;
    if (!stream || userId == null) return;
    try {
        await stream.muteAudio(userId);
        notifyParticipants();
    } catch (err) {
        const reason = err?.reason || err?.message || String(err);
        console.warn('[VideoSDK] muteAudio(userId):', reason);
        throw new Error('No se pudo silenciar al participante');
    }
};

/**
 * Zoom no permite activar el micrófono de otro: envía una solicitud de
 * consentimiento que el alumno debe aceptar.
 */
export const askToUnmuteParticipant = async (userId) => {
    if (!canMuteOthers(getLiveClassState())) {
        throw permissionError('pedir activar micrófonos');
    }
    const stream = videoSession.stream;
    if (!stream || userId == null) return;
    try {
        await stream.unmuteAudio(userId);
    } catch (err) {
        const reason = err?.reason || err?.message || String(err);
        console.warn('[VideoSDK] unmuteAudio(userId):', reason);
        throw new Error('No se pudo enviar la solicitud de micrófono');
    }
};

/** Silencia solo a los estudiantes: nunca al anfitrión ni a moderadores. */
export const muteAllStudents = async () => {
    if (!canMuteOthers(getLiveClassState())) {
        throw permissionError('silenciar a todos');
    }
    const stream = videoSession.stream;
    if (!stream) return 0;
    const targets = getParticipantsSnapshot().filter((p) => (
        !p.isHost && !p.isManager && !p.isSelf && !p.muted
    ));
    let muted = 0;
    for (const participant of targets) {
        try {
            await stream.muteAudio(participant.userId);
            muted += 1;
        } catch (err) {
            console.warn('[VideoSDK] muteAll:', err?.reason || err?.message || err);
        }
    }
    logLiveClassEvent('mute-all', { count: muted });
    notifyParticipants();
    return muted;
};

export const getRecordingClient = () => {
    try {
        return videoSession.client?.getRecordingClient?.() || null;
    } catch {
        return null;
    }
};

export const getCommandClient = () => {
    try {
        return videoSession.client?.getCommandClient?.() || null;
    } catch {
        return null;
    }
};

/**
 * Avisa cuando hay un cliente unido a la sesión (o null al cerrarse).
 * Se dispara de inmediato con el estado actual al suscribirse.
 */
export const onVideoSessionReady = (callback) => {
    videoSession.readyListeners.add(callback);
    if (videoSession.client && videoSession.joinedKey) {
        try {
            callback(videoSession.client);
        } catch (err) {
            console.warn('[VideoSDK] ready listener:', err?.message || err);
        }
    }
    return () => videoSession.readyListeners.delete(callback);
};

export const getVideoStream = () => videoSession.stream;
export const getVideoClient = () => videoSession.client;
export const getVideoParticipants = () => getParticipantsSnapshot();
export const getSubsessionClient = () => {
    try {
        return videoSession.client?.getSubsessionClient?.() || null;
    } catch {
        return null;
    }
};
export const getWhiteboardClient = () => {
    try {
        return videoSession.client?.getWhiteboardClient?.() || null;
    } catch {
        return null;
    }
};

const SHARE_VIDEO_ID = 'vc-share-self-video';
const SHARE_CANVAS_ID = 'vc-share-self-canvas';

const getLocalShareElement = (stream) => {
    const preferVideo = Boolean(stream?.isStartShareScreenWithVideoElement?.());
    const videoEl = document.getElementById(SHARE_VIDEO_ID);
    const canvasEl = document.getElementById(SHARE_CANVAS_ID);
    if (preferVideo && videoEl) return videoEl;
    if (canvasEl) return canvasEl;
    if (videoEl) return videoEl;
    return null;
};

/** true si este cliente está enviando pantalla (local). */
const isLocalSharing = () => {
    try {
        const stream = videoSession.stream;
        const me = videoSession.client?.getCurrentUserInfo?.();
        if (!stream || !me) return false;

        const status = stream.getShareStatus?.();
        if (status === 'sharing' || status === 'paused') return true;

        const shareId = stream.getActiveShareUserId?.();
        if (shareId != null && shareId !== 0 && shareId === me.userId) return true;

        // Fallback: estado que seteamos al iniciar share
        if (activeShareUserId != null && activeShareUserId === me.userId) return true;

        return false;
    } catch {
        return Boolean(activeShareUserId);
    }
};

/**
 * Congela la pantalla compartida del profesor: Zoom deja de enviar fotogramas y
 * los alumnos conservan el último en su vista, mientras el profesor sigue usando
 * su computadora sin que se vea. Solo aplica al share propio.
 */
export const setLocalShareFrozen = async (frozen) => {
    if (!canFreezeScreen(getLiveClassState())) {
        throw permissionError('congelar la pantalla');
    }
    const stream = videoSession.stream;
    if (!stream || !isLocalSharing()) {
        throw new Error('Comparte tu pantalla para poder congelarla');
    }

    try {
        if (frozen) await stream.pauseShareScreen();
        else await stream.resumeShareScreen();
    } catch (err) {
        const reason = err?.reason || err?.message || String(err);
        logLiveClassEvent(frozen ? 'share-pause-failed' : 'share-resume-failed', { reason });
        throw new Error(frozen
            ? 'Zoom no pudo congelar la pantalla. Intenta de nuevo.'
            : 'Zoom no pudo reanudar la pantalla. Intenta de nuevo.');
    }

    // Algunas versiones resuelven la promesa sin pausar de verdad: se confirma con el estado.
    const status = stream.getShareStatus?.();
    if (frozen && status && status !== 'paused') {
        logLiveClassEvent('share-pause-unconfirmed', { detail: { status } });
        try { await stream.resumeShareScreen(); } catch { /* ignore */ }
        throw new Error('Zoom no confirmó la pausa de la pantalla. Intenta de nuevo.');
    }

    patchLiveClassState({ screenFrozen: Boolean(frozen) });
    logLiveClassEvent(frozen ? 'share-paused' : 'share-resumed', {});
};

/** Vuelca al store el estado propio (medios, rol, share). */
const syncSelfState = () => {
    try {
        const client = videoSession.client;
        const stream = videoSession.stream;
        const me = client?.getCurrentUserInfo?.();
        patchLiveClassState({
            selfUserId: me?.userId ?? null,
            isHost: Boolean(me?.isHost || me?.isManager),
            microphoneEnabled: Boolean(me && me.muted === false),
            cameraEnabled: Boolean(me?.bVideoOn),
            screenShareEnabled: isLocalSharing(),
            shareLocked: Boolean(stream?.isShareLocked?.()),
        });
    } catch {
        // ignore
    }
};

/** Cierra sesión y destruye el singleton para poder init/join de nuevo. */
const safeDestroyClient = async () => {
    const client = videoSession.client;
    videoSession.suppressClosedNotify = true;
    try {
        if (client) {
            detachClientListeners(client);
            try {
                const stream = videoSession.stream || client.getMediaStream?.();
                if (stream) {
                    try { await stream.stopShareScreen?.(); } catch { /* ignore */ }
                    try { await stream.stopVideo?.(); } catch { /* ignore */ }
                    try { await stream.stopAudio?.(); } catch { /* ignore */ }
                }
            } catch { /* ignore */ }
            try {
                await client.leave();
            } catch (err) {
                console.warn('[VideoSDK] leave:', err?.message || err);
            }
        }
    } finally {
        try {
            await ZoomVideo.destroyClient();
        } catch {
            // ignore si no había cliente
        }
        videoSession.client = null;
        videoSession.stream = null;
        videoSession.joinedKey = null;
        activeShareUserId = null;
        notifyActiveShare();
        notifyReady(null);
        videoSession.suppressClosedNotify = false;
        notifyParticipants();
    }
};

/**
 * @param {{ notify?: boolean }} options
 */
export const leaveVideoSession = async ({ notify = false } = {}) => {
    // Salir es intencional: cancela cualquier reconexión pendiente.
    videoSession.reconnectDisabled = true;
    videoSession.lastCredentials = null;
    cancelReconnect();
    clearSpeakingTimers();

    if (videoSession.leavePromise) {
        await videoSession.leavePromise;
        return;
    }

    if (videoSession.joinPromise) {
        videoSession.cancelJoin = true;
        try {
            await videoSession.joinPromise;
        } catch {
            // join cancelado
        }
        // join cancelado ya destruye el cliente; asegura limpieza
        if (videoSession.client || videoSession.joinedKey) {
            videoSession.leavePromise = safeDestroyClient();
            try {
                await videoSession.leavePromise;
            } finally {
                videoSession.leavePromise = null;
            }
        }
        resetLiveClassState();
        if (notify) notifyClosed();
        return;
    }

    if (!videoSession.joinedKey && !videoSession.client) {
        try {
            await ZoomVideo.destroyClient();
        } catch {
            // ignore
        }
        resetLiveClassState();
        return;
    }

    videoSession.leavePromise = (async () => {
        await safeDestroyClient();
        resetLiveClassState();
        if (notify) notifyClosed();
    })();

    try {
        await videoSession.leavePromise;
    } finally {
        videoSession.leavePromise = null;
    }
};

const EMPTY_DEVICES = {
    mics: [],
    speakers: [],
    cameras: [],
    activeMic: '',
    activeSpeaker: '',
    activeCamera: '',
};

const mapDevice = (d) => ({ deviceId: d.deviceId, label: d.label || '' });

/** Lee las listas de dispositivos del stream activo (getters síncronos del SDK). */
const readDeviceState = () => {
    const stream = videoSession.stream;
    if (!stream) return EMPTY_DEVICES;
    try {
        return {
            mics: (stream.getMicList?.() || []).map(mapDevice),
            speakers: (stream.getSpeakerList?.() || []).map(mapDevice),
            cameras: (stream.getCameraList?.() || []).map(mapDevice),
            activeMic: stream.getActiveMicrophone?.() || '',
            activeSpeaker: stream.getActiveSpeaker?.() || '',
            activeCamera: stream.getActiveCamera?.() || '',
        };
    } catch {
        return EMPTY_DEVICES;
    }
};

const isDev = () => process.env.NODE_ENV !== 'production';

const decodeJwtPayload = (token) => {
    try {
        const part = String(token || '').split('.')[1];
        if (!part) return null;
        const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json);
    } catch {
        return null;
    }
};

/** Traza de join: consola en desarrollo y log persistente siempre. */
const logJoin = (phase, data = {}) => {
    if (isDev()) {
        console.log(`[VideoSDK join] ${phase}`, {
            ts: new Date().toISOString(),
            cancelJoin: videoSession.cancelJoin,
            hasClient: Boolean(videoSession.client),
            ...data,
        });
    }
    logLiveClassEvent(`join:${phase}`, data);
};

/**
 * Crea cliente, se une y deja la sesión lista.
 * Se usa tanto en el join inicial como en cada reconexión.
 */
const runJoin = async (credentials, { sessionKey, isReconnect = false }) => {
    const attemptId = `join-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    logJoin('start', {
        attemptId,
        isReconnect,
        sessionName: credentials?.sessionName,
        isHost: credentials?.isHost,
        role: credentials?.role,
        userName: credentials?.userName,
        passcode_len: String(credentials?.sessionPasscode || '').length,
        signature_len: String(credentials?.signature || '').length,
    });

    if (videoSession.cancelJoin) {
        logJoin('aborted-before-destroy', { attemptId });
        videoSession.cancelJoin = false;
        return null;
    }

    // Limpia cualquier singleton residual antes de recrear.
    try {
        await ZoomVideo.destroyClient();
        logJoin('destroyClient-ok', { attemptId });
    } catch (err) {
        logJoin('destroyClient-noop', { attemptId, message: err?.message || String(err) });
    }

    if (videoSession.cancelJoin) {
        logJoin('aborted-after-destroy', { attemptId });
        videoSession.cancelJoin = false;
        return null;
    }

    const topic = String(credentials.sessionName || '').trim().toLowerCase();
    const token = String(credentials.signature || '').trim();
    const userName = String(credentials.userName || 'Participante').trim() || 'Participante';
    const password = String(credentials.sessionPasscode || '');

    if (!topic || !token) {
        logJoin('missing-credentials', { attemptId, topic, token_len: token.length });
        throw new Error('Faltan sessionName o JWT para unirse a Zoom');
    }

    const payload = decodeJwtPayload(token);
    const now = Math.round(Date.now() / 1000);
    logJoin('jwt-decoded', {
        attemptId,
        topic,
        userName,
        passcode_len: password.length,
        tpc_match: payload ? String(payload.tpc).trim().toLowerCase() === topic : false,
        token_len: token.length,
        role_type: payload?.role_type,
        expires_in_sec: payload?.exp != null ? payload.exp - now : null,
    });

    if (!payload?.app_key || payload.tpc == null) {
        logJoin('jwt-invalid-structure', { attemptId });
        throw new Error('JWT de Zoom inválido (falta app_key o tpc)');
    }
    const jwtTpc = String(payload.tpc).trim().toLowerCase();
    if (jwtTpc !== topic) {
        logJoin('jwt-tpc-mismatch', { attemptId, jwtTpc, topic });
        throw new Error(`JWT tpc (${jwtTpc}) no coincide con sessionName (${topic})`);
    }

    const client = ZoomVideo.createClient();
    logJoin('init-begin', { attemptId });
    await client.init('en-US', 'Global', {
        patchJsMedia: true,
        leaveOnPageUnload: true,
    });
    logJoin('init-ok', { attemptId });

    if (videoSession.cancelJoin) {
        logJoin('aborted-after-init', { attemptId });
        videoSession.cancelJoin = false;
        try { await client.leave(); } catch { /* ignore */ }
        try { await ZoomVideo.destroyClient(); } catch { /* ignore */ }
        return null;
    }

    const onConn = (payloadConn) => {
        logJoin('connection-change', {
            attemptId,
            state: payloadConn?.state,
            reason: payloadConn?.reason,
            errorCode: payloadConn?.errorCode,
        });
    };
    try {
        client.on('connection-change', onConn);
    } catch {
        // ignore
    }

    try {
        logJoin('client.join-begin', {
            attemptId,
            topic,
            userName,
            passcode_len: password.length,
            token_len: token.length,
            role_type: payload.role_type,
        });
        await client.join(topic, token, userName, password);
        logJoin('client.join-ok', { attemptId });
    } catch (err) {
        logJoin('client.join-FAILED', {
            attemptId,
            reason: err?.reason,
            message: err?.message,
            errorCode: err?.errorCode,
            type: err?.type,
            topic,
            jwtTpc,
            token_len: token.length,
            role_type: payload.role_type,
            expires_in_sec: payload?.exp != null ? payload.exp - now : null,
        });
        try { client.off('connection-change', onConn); } catch { /* ignore */ }
        try { await client.leave(); } catch { /* ignore */ }
        try { await ZoomVideo.destroyClient(); } catch { /* ignore */ }
        const reason = err?.reason || err?.message || String(err);
        throw Object.assign(new Error(reason), {
            reason,
            errorCode: err?.errorCode,
            type: err?.type,
        });
    }

    try {
        client.off('connection-change', onConn);
    } catch {
        // ignore
    }

    if (videoSession.cancelJoin) {
        logJoin('aborted-after-join', { attemptId });
        videoSession.cancelJoin = false;
        try { await client.leave(); } catch { /* ignore */ }
        try { await ZoomVideo.destroyClient(); } catch { /* ignore */ }
        return null;
    }

    const stream = client.getMediaStream();
    videoSession.client = client;
    videoSession.stream = stream;
    videoSession.joinedKey = sessionKey;
    videoSession.lastCredentials = credentials;
    attachClientListeners(client);
    patchLiveClassState({ connection: CONNECTION_STATE.CONNECTED });
    logJoin('session-ready', {
        attemptId,
        userId: client.getCurrentUserInfo?.()?.userId,
        participantCount: client.getAllUser?.()?.length,
        isHost: Boolean(client.getCurrentUserInfo?.()?.isHost),
        isManager: Boolean(client.getCurrentUserInfo?.()?.isManager),
    });

    if (credentials?.isHost) {
        try {
            const me = client.getCurrentUserInfo?.();
            if (me && !me.isHost && typeof client.reclaimHost === 'function') {
                logJoin('reclaimHost-begin', { attemptId });
                await client.reclaimHost();
                logJoin('reclaimHost-ok', {
                    attemptId,
                    isHost: Boolean(client.getCurrentUserInfo?.()?.isHost),
                });
            }
        } catch (err) {
            logJoin('reclaimHost-fail', {
                attemptId,
                message: err?.reason || err?.message || String(err),
            });
        }
        try {
            if (typeof stream.lockShare === 'function') {
                await stream.lockShare(true);
                logJoin('lockShare-ok', { attemptId });
            }
        } catch (err) {
            logJoin('lockShare-fail', {
                attemptId,
                message: err?.reason || err?.message || String(err),
            });
        }
    }

    notifyReady(client);
    void probeActiveShare();

    // Al reconectar se recupera la intención previa; al entrar, la del pre-join.
    let joinWithMic;
    let joinWithCamera;
    let micId = null;
    let cameraId = null;

    if (isReconnect) {
        joinWithMic = videoSession.desired.audioOn;
        joinWithCamera = videoSession.desired.videoOn;
    } else {
        const preferredDevices = getPreJoinDevicePreferences();
        joinWithMic = preferredDevices.joinWithMic !== false;
        joinWithCamera = preferredDevices.joinWithCamera !== false;
        micId = preferredDevices.micId || null;
        cameraId = preferredDevices.cameraId || null;
        videoSession.desired = { audioOn: joinWithMic, videoOn: joinWithCamera };
    }

    try {
        await stream.startAudio({ mute: !joinWithMic });
        logJoin('startAudio-ok', { attemptId, muted: !joinWithMic });
    } catch (err) {
        console.warn('[VideoSDK] Audio no disponible:', err?.message || err);
        logJoin('startAudio-fail', { attemptId, message: err?.message || String(err) });
    }

    if (joinWithCamera) {
        try {
            await stream.startVideo({ mirrored: true });
            logJoin('startVideo-ok', { attemptId });
        } catch (err) {
            console.warn('[VideoSDK] Cámara no disponible:', err?.message || err);
            logJoin('startVideo-fail', { attemptId, message: err?.message || String(err) });
        }
    } else {
        logJoin('startVideo-skipped', { attemptId });
    }

    if (micId) {
        try {
            await stream.switchMicrophone(micId);
            logJoin('switchMicrophone-ok', { attemptId, deviceId: micId });
        } catch (err) {
            console.warn('[VideoSDK] switchMicrophone (pre-join):', err?.reason || err?.message || err);
        }
    }
    if (cameraId && joinWithCamera) {
        try {
            await stream.switchCamera(cameraId);
            logJoin('switchCamera-ok', { attemptId, deviceId: cameraId });
        } catch (err) {
            console.warn('[VideoSDK] switchCamera (pre-join):', err?.reason || err?.message || err);
        }
    }
    if (!isReconnect) {
        clearPreJoinDevicePreferences();
    }

    notifyParticipants();
    syncSelfState();
    setRecordingStatusValue(readRecordingStatus(client));
    return client;
};

/** El JWT caduca: cada reintento pide credenciales frescas al backend. */
const refreshJoinCredentials = async (credentials) => {
    const sessionId = credentials?.session?._id;
    if (!sessionId) return credentials;
    try {
        // socketId se excluye del control de sesiones duplicadas: sin él el
        // backend creería que la misma cuenta ya ocupa la clase.
        const { data } = await axiosPrivate.post(`/api/zoom/sessions/${sessionId}/join`, {
            socketId: getSocket()?.id || undefined,
        });
        if (data?.signature && data?.sessionName) {
            logLiveClassEvent('reconnect-credentials-refreshed', {});
            return data;
        }
    } catch (err) {
        logLiveClassEvent('reconnect-credentials-failed', {
            reason: err?.response?.data?.message || err?.message || String(err),
        });
        // Si el backend falla pero el JWT sigue vigente, se intenta con el viejo.
        const payload = decodeJwtPayload(credentials.signature);
        const now = Math.round(Date.now() / 1000);
        if (payload?.exp && payload.exp - now < 60) {
            throw err;
        }
    }
    return credentials;
};

const attemptReconnect = async () => {
    if (videoSession.reconnecting) return;
    const credentials = videoSession.lastCredentials;
    if (!credentials || videoSession.reconnectDisabled) return;

    videoSession.reconnecting = true;
    const attempt = videoSession.reconnectAttempt;
    logLiveClassEvent('reconnect-attempt', { attempt });

    try {
        const fresh = await refreshJoinCredentials(credentials);
        const sessionKey = getSessionKey(fresh);
        videoSession.cancelJoin = false;
        videoSession.joinPromise = runJoin(fresh, { sessionKey, isReconnect: true })
            .finally(() => {
                videoSession.joinPromise = null;
            });
        const client = await videoSession.joinPromise;
        if (!client) throw new Error('Reconexión cancelada');

        cancelReconnect();
        patchLiveClassState({ connection: CONNECTION_STATE.CONNECTED });
        logLiveClassEvent('reconnect-ok', { attempt });
    } catch (err) {
        logLiveClassEvent('reconnect-failed', {
            attempt,
            reason: err?.reason || err?.message || String(err),
        });
        videoSession.reconnecting = false;
        scheduleReconnect();
        return;
    } finally {
        videoSession.reconnecting = false;
    }
};

const scheduleReconnect = () => {
    if (videoSession.reconnectTimer || videoSession.reconnecting) return;
    if (videoSession.reconnectDisabled || !videoSession.lastCredentials) return;

    const attempt = videoSession.reconnectAttempt;
    if (attempt >= RECONNECT_DELAYS.length) {
        logLiveClassEvent('reconnect-exhausted', { attempt });
        patchLiveClassState({ connection: CONNECTION_STATE.FAILED });
        videoSession.lastCredentials = null;
        notifyClosed();
        return;
    }

    videoSession.reconnectAttempt = attempt + 1;
    patchLiveClassState({
        connection: CONNECTION_STATE.RECONNECTING,
        reconnectAttempt: attempt + 1,
    });
    videoSession.reconnectTimer = setTimeout(() => {
        videoSession.reconnectTimer = null;
        void attemptReconnect();
    }, RECONNECT_DELAYS[attempt]);
};

/** Vuelve la red o la pestaña: no esperar el backoff completo. */
const retryReconnectNow = () => {
    if (!videoSession.reconnectTimer) return;
    clearTimeout(videoSession.reconnectTimer);
    videoSession.reconnectTimer = null;
    void attemptReconnect();
};

if (typeof window !== 'undefined') {
    window.addEventListener('online', retryReconnectNow);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') retryReconnectNow();
    });
}

export const useVideoSession = () => {
    const [mediaState, setMediaState] = useState({
        audioOn: false,
        videoOn: false,
        sharing: false,
    });
    const [devices, setDevices] = useState(EMPTY_DEVICES);
    const [recordingStatus, setRecordingStatus] = useState('Stopped');
    const [recordingBusy, setRecordingBusy] = useState(false);

    const refreshDevices = useCallback(() => {
        const next = readDeviceState();
        setDevices((prev) => (
            JSON.stringify(prev) === JSON.stringify(next) ? prev : next
        ));
    }, []);

    /** Espeja el store en el state local (compatibilidad con los consumidores). */
    const syncMediaState = useCallback(() => {
        syncSelfState();
    }, []);

    const joinSession = useCallback(async (credentials) => {
        if (!isZoomEnabled()) {
            throw new Error('Las videollamadas no están habilitadas');
        }

        if (videoSession.leavePromise) {
            await videoSession.leavePromise;
        }

        if (videoSession.joinPromise) {
            try {
                await videoSession.joinPromise;
            } catch {
                // ignore
            }
        }

        const sessionKey = getSessionKey(credentials);
        if (videoSession.joinedKey === sessionKey && videoSession.client) {
            return videoSession.client;
        }

        if (videoSession.joinedKey || videoSession.client) {
            await leaveVideoSession({ notify: false });
        }

        videoSession.cancelJoin = false;
        videoSession.reconnectDisabled = false;
        videoSession.reconnectAttempt = 0;
        setLiveClassLogContext({
            sessionId: credentials?.session?._id || null,
            courseId: credentials?.session?.courseId || null,
        });
        patchLiveClassState({
            connection: CONNECTION_STATE.CONNECTING,
            reconnectAttempt: 0,
            screenFrozen: false,
            raisedHandUserIds: [],
        });

        videoSession.joinPromise = runJoin(credentials, { sessionKey })
            .finally(() => {
                videoSession.joinPromise = null;
            });

        try {
            return await videoSession.joinPromise;
        } catch (err) {
            patchLiveClassState({ connection: CONNECTION_STATE.FAILED });
            throw err;
        }
    }, []);

    const leaveSession = useCallback(async () => {
        await leaveVideoSession({ notify: false });
        setMediaState({ audioOn: false, videoOn: false, sharing: false });
    }, []);

    const toggleAudio = useCallback(async () => {
        const stream = videoSession.stream;
        const me = videoSession.client?.getCurrentUserInfo?.();
        if (!stream || !me) return;
        try {
            if (me.muted) {
                await stream.unmuteAudio();
                videoSession.desired.audioOn = true;
            } else {
                await stream.muteAudio();
                videoSession.desired.audioOn = false;
            }
            syncMediaState();
            notifyParticipants();
        } catch (err) {
            console.warn('[VideoSDK] toggleAudio:', err?.message || err);
            throw err;
        }
    }, [syncMediaState]);

    const toggleVideo = useCallback(async () => {
        const stream = videoSession.stream;
        const me = videoSession.client?.getCurrentUserInfo?.();
        if (!stream || !me) return;
        try {
            if (me.bVideoOn) {
                await stream.stopVideo();
                videoSession.desired.videoOn = false;
            } else {
                await stream.startVideo({ mirrored: true });
                videoSession.desired.videoOn = true;
            }
            syncMediaState();
            notifyParticipants();
        } catch (err) {
            console.warn('[VideoSDK] toggleVideo:', err?.message || err);
            throw err;
        }
    }, [syncMediaState]);

    const stopShare = useCallback(async () => {
        const stream = videoSession.stream;
        if (!stream) {
            activeShareUserId = null;
            notifyActiveShare();
            syncMediaState();
            return;
        }
        try {
            await stream.stopShareScreen();
        } catch (err) {
            // Si Zoom ya no tiene share activo, igual limpiamos UI.
            console.warn('[VideoSDK] stopShareScreen:', err?.reason || err?.message || err);
        } finally {
            activeShareUserId = null;
            patchLiveClassState({ screenFrozen: false });
            notifyActiveShare();
            syncMediaState();
            await reassertDesiredMedia('share-stop');
        }
    }, [syncMediaState]);

    const toggleShare = useCallback(async () => {
        const stream = videoSession.stream;
        const me = videoSession.client?.getCurrentUserInfo?.();
        if (!stream || !me) {
            throw new Error('Aún no hay sesión de video activa');
        }

        // Si ya estamos compartiendo (o la UI cree que sí), SIEMPRE detener.
        if (isLocalSharing()) {
            await stopShare();
            return;
        }

        try {
            const el = getLocalShareElement(stream);
            if (!el) {
                throw new Error(
                    'No hay superficie para renderizar la pantalla compartida. Recarga e inténtalo de nuevo.',
                );
            }

            const videoEl = document.getElementById(SHARE_VIDEO_ID);
            const canvasEl = document.getElementById(SHARE_CANVAS_ID);
            if (videoEl) videoEl.style.display = el === videoEl ? 'block' : 'none';
            if (canvasEl) canvasEl.style.display = el === canvasEl ? 'block' : 'none';

            await stream.startShareScreen(el);
            activeShareUserId = me.userId;
            logLiveClassEvent('share-started', {});
            notifyActiveShare();
            syncMediaState();
            // Compartir puede apagar mic/cámara: se devuelven a como estaban.
            await reassertDesiredMedia('share-start');
        } catch (err) {
            const reason = err?.reason || err?.message || String(err);
            console.warn('[VideoSDK] toggleShare:', reason, err);
            logLiveClassEvent('share-start-failed', { reason });

            // Si Zoom dice que ya hay share, forzar stop en lugar de fallar.
            if (/already|sharing|in progress|INVALID_OPERATION|screen share state/i.test(reason)) {
                await stopShare();
                return;
            }
            if (/Permission|NotAllowed|denied|PERMISSION/i.test(reason)) {
                throw new Error('El navegador bloqueó compartir pantalla. Permite el acceso e inténtalo de nuevo.');
            }
            if (/display-capture|getDisplayMedia|not support|platform/i.test(reason)) {
                throw new Error('Este navegador o contexto no permite compartir pantalla (usa Chrome/Edge en localhost o HTTPS).');
            }
            if (/privilege|host/i.test(reason)) {
                throw new Error('No tienes permiso para compartir pantalla en esta sesión.');
            }
            throw new Error(reason || 'No se pudo compartir la pantalla');
        }
    }, [stopShare, syncMediaState]);

    const selectMicrophone = useCallback(async (deviceId) => {
        const stream = videoSession.stream;
        if (!stream || !deviceId) return;
        try {
            await stream.switchMicrophone(deviceId);
            refreshDevices();
        } catch (err) {
            console.warn('[VideoSDK] switchMicrophone:', err?.reason || err?.message || err);
            throw new Error('No se pudo cambiar el micrófono');
        }
    }, [refreshDevices]);

    const selectSpeaker = useCallback(async (deviceId) => {
        const stream = videoSession.stream;
        if (!stream || !deviceId) return;
        try {
            await stream.switchSpeaker(deviceId);
            refreshDevices();
        } catch (err) {
            console.warn('[VideoSDK] switchSpeaker:', err?.reason || err?.message || err);
            throw new Error('No se pudo cambiar el altavoz');
        }
    }, [refreshDevices]);

    const selectCamera = useCallback(async (deviceId) => {
        const stream = videoSession.stream;
        if (!stream || !deviceId) return;
        try {
            await stream.switchCamera(deviceId);
            refreshDevices();
        } catch (err) {
            console.warn('[VideoSDK] switchCamera:', err?.reason || err?.message || err);
            throw new Error('No se pudo cambiar la cámara');
        }
    }, [refreshDevices]);

    const syncRecordingStatus = useCallback(() => {
        setRecordingStatusValue(readRecordingStatus());
    }, []);

    const runRecordingAction = useCallback(async (method, label) => {
        if (!canControlRecording(getLiveClassState())) {
            throw permissionError('controlar la grabación');
        }
        const recordingClient = getRecordingClient();
        if (!recordingClient || typeof recordingClient[method] !== 'function') {
            throw new Error('Aún no hay sesión de video activa');
        }
        setRecordingBusy(true);
        patchLiveClassState({ recordingBusy: true });
        try {
            const result = await recordingClient[method]();
            if (result && typeof result === 'object' && result.reason) {
                throw new Error(result.reason || result.message || `No se pudo ${label}`);
            }
            logLiveClassEvent(`recording-${method}`, {});
        } catch (err) {
            const reason = err?.reason || err?.message || String(err);
            console.warn(`[VideoSDK] ${method}:`, reason, err);
            if (/not enabled|cloud recording|account setting/i.test(reason)) {
                throw new Error('La grabación en la nube no está habilitada en la cuenta de Zoom.');
            }
            if (/privilege|host|permission/i.test(reason)) {
                throw new Error('Solo el anfitrión puede controlar la grabación.');
            }
            throw new Error(reason || `No se pudo ${label}`);
        } finally {
            setRecordingBusy(false);
            patchLiveClassState({ recordingBusy: false });
            syncRecordingStatus();
        }
    }, [syncRecordingStatus]);

    const startCloudRecording = useCallback(async () => {
        const recordingClient = getRecordingClient();
        if (recordingClient
            && typeof recordingClient.canStartRecording === 'function'
            && !recordingClient.canStartRecording()) {
            throw new Error('No se puede grabar ahora. Revisa que la grabación en la nube esté habilitada en Zoom.');
        }
        await runRecordingAction('startCloudRecording', 'iniciar la grabación');
    }, [runRecordingAction]);

    const stopCloudRecording = useCallback(
        () => runRecordingAction('stopCloudRecording', 'detener la grabación'),
        [runRecordingAction],
    );

    const pauseCloudRecording = useCallback(
        () => runRecordingAction('pauseCloudRecording', 'pausar la grabación'),
        [runRecordingAction],
    );

    const resumeCloudRecording = useCallback(
        () => runRecordingAction('resumeCloudRecording', 'reanudar la grabación'),
        [runRecordingAction],
    );

    // Mantiene las listas de dispositivos al día mientras hay sesión.
    useEffect(() => {
        let detachDeviceListener = null;
        const attach = (client) => {
            if (detachDeviceListener) {
                detachDeviceListener();
                detachDeviceListener = null;
            }
            refreshDevices();
            if (!client) return;
            const onDeviceChange = () => refreshDevices();
            try {
                client.on('device-change', onDeviceChange);
            } catch {
                // ignore
            }
            detachDeviceListener = () => {
                try {
                    client.off('device-change', onDeviceChange);
                } catch {
                    // ignore
                }
            };
        };
        const unsub = onVideoSessionReady(attach);
        return () => {
            unsub();
            if (detachDeviceListener) detachDeviceListener();
        };
    }, [refreshDevices]);

    /**
     * El estado de medios se actualiza por eventos del SDK (current-audio-change,
     * video-capturing-change, user-updated, share-*) que escriben en el store.
     * El intervalo largo es solo una red de seguridad si algún evento se pierde.
     */
    useEffect(() => {
        const readStore = () => {
            const live = getLiveClassState();
            setMediaState((prev) => {
                const next = {
                    audioOn: live.microphoneEnabled,
                    videoOn: live.cameraEnabled,
                    sharing: live.screenShareEnabled,
                };
                const same = prev.audioOn === next.audioOn
                    && prev.videoOn === next.videoOn
                    && prev.sharing === next.sharing;
                return same ? prev : next;
            });
        };

        const unsubscribe = subscribeLiveClass(readStore);
        readStore();
        const safetyId = setInterval(() => {
            if (videoSession.client) syncSelfState();
        }, 15000);
        return () => {
            unsubscribe();
            clearInterval(safetyId);
        };
    }, []);

    useEffect(() => onCloudRecordingChange(setRecordingStatus), []);

    useEffect(() => {
        const unsub = onVideoSessionReady(() => {
            syncRecordingStatus();
        });
        const unsubClosed = onVideoSessionClosed(() => {
            setRecordingStatusValue('Stopped');
            setRecordingBusy(false);
            setMediaState({ audioOn: false, videoOn: false, sharing: false });
        });
        return () => {
            unsub();
            unsubClosed();
        };
    }, [syncRecordingStatus]);

    return {
        joinSession,
        leaveSession,
        toggleAudio,
        toggleVideo,
        toggleShare,
        stopShare,
        startCloudRecording,
        stopCloudRecording,
        pauseCloudRecording,
        resumeCloudRecording,
        muteParticipant,
        askToUnmuteParticipant,
        muteAllStudents,
        mediaState,
        devices,
        recordingStatus,
        recordingBusy,
        refreshDevices,
        selectMicrophone,
        selectSpeaker,
        selectCamera,
        isZoomEnabled: isZoomEnabled(),
        VideoQuality,
    };
};

export { RECORDING_STATE, CONNECTION_STATE };
